"use client";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import UnifiedItemSearch from "./UnifiedItemSearch";
import AlertDialog from "./AlertDialog";

const API = "https://erp-xe9u.onrender.com/api";
const cls = (...c) => c.filter(Boolean).join(" ");

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS = {
  Pending: {
    pill: "bg-yellow-900 text-yellow-300 border border-yellow-700",
    bar: "bg-yellow-500",
    label: "Pending",
  },
  Approved: {
    pill: "bg-green-900 text-green-300 border border-green-700",
    bar: "bg-green-500",
    label: "Approved",
  },
  Rejected: {
    pill: "bg-red-900 text-red-300 border border-red-700",
    bar: "bg-red-500",
    label: "Rejected",
  },
  Converted: {
    pill: "bg-purple-900 text-purple-300 border border-purple-700",
    bar: "bg-purple-500",
    label: "Converted",
  },
  Draft: {
    pill: "bg-gray-700 text-gray-300 border border-gray-600",
    bar: "bg-gray-500",
    label: "Draft",
  },
};

function StatusPill({ status, size = "sm" }) {
  const s = STATUS[status] || STATUS.Draft;
  return (
    <span
      className={cls(
        "inline-flex items-center rounded-full font-semibold",
        size === "xs" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-0.5 text-xs",
        s.pill,
      )}
    >
      {status}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── SEARCH ENGINE (ported from mobile material-request-foreman-v3) ──────────
// ═══════════════════════════════════════════════════════════════════════════════

// Shared merge helper — paste into both COFormV3 and CODetailV3
const buildCombinedPDF = async (coBlob, tandmBlobs) => {
  if (!tandmBlobs.length) return coBlob;
  const { mergePDFBlobs } = await import("./TimeAndMaterialTab3");
  return mergePDFBlobs([coBlob, ...tandmBlobs.map((t) => t.blob)]);
};
// Normalize smart/curly quotes and prime symbols to plain ASCII
function normalizeSize(s) {
  return (s || "")
    .replace(/[\u2018\u2019\u2032\u02BC`]/g, "'")
    .replace(/[\u201C\u201D\u2033]/g, '"');
}

// Fraction / inch string → numeric inches
function inchStrToInches(raw) {
  const s = normalizeSize(raw || "").trim();
  if (!s) return null;
  let m = s.match(/^(\d+)\s*-\s*(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10);
  m = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10);
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1], 10) / parseInt(m[2], 10);
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
}

// Token → feet
function tokenToFeet(raw) {
  const s = normalizeSize(raw || "").trim();
  if (!s) return null;
  if (s.includes("'")) {
    const m = s.match(/^(\d+(?:\.\d+)?)\s*'[\s-]*(.*?)\s*"?\s*$/);
    if (!m) return null;
    const ft = parseFloat(m[1]);
    const inchPart = (m[2] || "").trim();
    if (!inchPart) return ft;
    const inches = inchStrToInches(inchPart);
    return inches === null ? null : ft + inches / 12;
  }
  if (s.endsWith('"')) {
    const inches = inchStrToInches(s.slice(0, -1).trim());
    return inches === null ? null : inches / 12;
  }
  const m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  const inches = inchStrToInches(s);
  return inches === null ? null : inches / 12;
}

// Parse coverage from a size string + uom (returns SF or LF value, or null)
function parseSizeCoverage(size, uom) {
  if (!size || !uom) return null;
  const u = uom.trim().toUpperCase();
  const s = normalizeSize(size.trim());
  if (u.includes("SF")) {
    const parts = s.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const a = tokenToFeet(parts[0]);
      const b = tokenToFeet(parts[1]);
      if (a !== null && b !== null) return Math.round(a * b * 10000) / 10000;
    }
    return null;
  }
  if (u.includes("LF")) {
    const ft = tokenToFeet(s);
    return ft !== null ? Math.round(ft * 10000) / 10000 : null;
  }
  return null;
}

const isCoverageBased = (uom = "") =>
  ["SF", "LF"].some((u) => uom.trim().toUpperCase().includes(u));

// ── Structured scoring helpers ────────────────────────────────────────────────
const QUOTE_RE = /['"\u2018\u2019\u201c\u201d\u2032\u2033`]/g;
const stripQuotes = (s) => String(s == null ? "" : s).replace(QUOTE_RE, "");
const norm = (s) => stripQuotes(s).toLowerCase().replace(/\s+/g, " ").trim();
const searchTokens = (s) =>
  norm(s)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);

function toVal(s) {
  if (s == null) return null;
  const t = stripQuotes(String(s)).trim().toLowerCase();
  let m = t.match(/^(\d+)[-\s](\d+)\/(\d+)$/);
  if (m) return +m[1] + +m[2] / +m[3];
  m = t.match(/^(\d+)\/(\d+)$/);
  if (m) return +m[1] / +m[2];
  m = t.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
}

const approx = (a, b, tol = 0.06) =>
  a != null && b != null && Math.abs(a - b) <= tol;

const MIL2GA = { 18: "25", 33: "20", 43: "18", 54: "16", 68: "14", 97: "12" };

const WIDTH_SHORTHAND = {
  158: 1.625,
  162: 1.625,
  212: 2.5,
  250: 2.5,
  312: 3.5,
  350: 3.5,
  358: 3.625,
  362: 3.625,
  400: 4,
  512: 5.5,
  550: 5.5,
  600: 6,
  800: 8,
  1000: 10,
  1200: 12,
};

const WEBCODE = {
  125: 1.25,
  137: 1.375,
  150: 1.5,
  162: 1.625,
  212: 2.5,
  250: 2.5,
  350: 3.5,
  358: 3.625,
  362: 3.625,
  400: 4,
  550: 5.5,
  600: 6,
  800: 8,
  1000: 10,
  1200: 12,
};

const TYPE_RULES = [
  [
    "insulation",
    [
      /insulation/,
      /comfortbatt/,
      /rockwool/,
      /roxul/,
      /mineral wool/,
      /safe.?n.?sound/,
      /\bbatt\b/,
      /fiberglass/,
    ],
  ],
  ["slotted track", [/slotted track/]],
  ["flex track", [/flex track/]],
  ["j runner", [/j-?\s*runner/]],
  ["ch stud", [/\bch stud/]],
  ["carrying channel", [/carrying channel/]],
  ["furring channel", [/furring/]],
  ["resilient channel", [/resilient/]],
  ["z bar", [/\bz ?bar/]],
  ["hanger wire", [/hanger wire/]],
  ["tie wire", [/tie wire/]],
  ["angle", [/\bangle\b/, /d700/, /\bl[ -]track\b/]],
  ["track", [/\btracks?\b/]],
  ["stud", [/\bstuds?\b/]],
  ["screw", [/screw/]],
  ["drywall", [/drywall/, /gypsum/, /\bdensglass\b/, /sheathing/]],
];
const TRACK_FAMILY = new Set([
  "track",
  "slotted track",
  "flex track",
  "j runner",
]);
const SEARCH_ALIASES = {
  dw: ["drywall"],
  gb: ["gypsum"],
  gyp: ["gypsum"],
  std: ["stud"],
  mtl: ["metal"],
};

function detectType(text) {
  const t = norm(text);
  for (const [name, pats] of TYPE_RULES)
    if (pats.some((p) => p.test(t))) return name;
  return null;
}
function detectGauge(text) {
  const t = norm(text);
  let m = t.match(/(\d{1,2})\s*ga(?:uge)?\b/);
  if (m) return String(parseInt(m[1], 10));
  m = t.match(/(\d{2})\s*mil\b/);
  if (m) return MIL2GA[m[1]] || null;
  return null;
}
function detectLegFlange(text) {
  const m = norm(text).match(
    /(\d+(?:[-\s]\d+\/\d+|\/\d+)?)\s*(?:leg|flange|fl)\b/,
  );
  return m ? toVal(m[1]) : null;
}

function itemSpec(item) {
  const code = stripQuotes(item.itemCode || "").toUpperCase();
  const desc =
    item.description || item.costCodeDescription || item.itemDescription || "";
  const spec = { depth: null, type: null, gauge: null, legflange: null };
  const m = code.match(/^(\d{3,4})([ST])(\d{2,3})-(\d{2,3})/);
  if (m) {
    spec.depth = WEBCODE[m[1]] ?? null;
    spec.type = m[2] === "S" ? "stud" : "track";
    spec.legflange = WEBCODE[m[3]] ?? null;
    spec.gauge = MIL2GA[m[4]] || null;
  }
  const dtype = detectType(desc);
  if (dtype) spec.type = dtype;
  if (spec.depth == null) {
    const md = desc
      .toLowerCase()
      .match(/^\s*(\d+(?:-\d+\/\d+)?)\s*["\u201d\u2033]/);
    if (md) spec.depth = toVal(md[1]);
  }
  if (spec.gauge == null) spec.gauge = detectGauge(desc);
  if (spec.legflange == null) spec.legflange = detectLegFlange(desc);
  return spec;
}

function querySpec(q) {
  const t = norm(q);
  const spec = {
    depth: null,
    type: detectType(q),
    gauge: detectGauge(q),
    legflange: detectLegFlange(q),
  };
  const t2 = t
    .replace(/(\d{1,2})\s*ga(?:uge)?\b/g, " ")
    .replace(/(\d{2})\s*mil\b/g, " ")
    .replace(/(\d+(?:[-\s]\d+\/\d+|\/\d+)?)\s*(?:leg|flange|fl)\b/g, " ");
  const lead = searchTokens(t2);
  let digits = "";
  let i = 0;
  while (i < lead.length && /^\d+$/.test(lead[i])) {
    digits += lead[i];
    i++;
  }
  if (digits) {
    spec.depth =
      WIDTH_SHORTHAND[digits] != null
        ? WIDTH_SHORTHAND[digits]
        : toVal(lead[0]);
  }
  return spec;
}

function scoreItem(item, rawQuery) {
  const qs = querySpec(rawQuery);
  const framing = !!(qs.type || qs.gauge || qs.legflange != null);
  let s = 0;

  if (framing) {
    const isp = itemSpec(item);
    if (qs.type && isp.type) {
      if (qs.type === isp.type) s += 200;
      else if (TRACK_FAMILY.has(qs.type) && TRACK_FAMILY.has(isp.type)) s += 70;
      else s -= 220;
    } else if (qs.type && !isp.type) {
      s -= 40;
    }
    if (qs.depth != null && isp.depth != null)
      s += approx(qs.depth, isp.depth) ? 150 : -160;
    if (qs.gauge && isp.gauge) s += qs.gauge === isp.gauge ? 100 : -110;
    if (qs.legflange != null && isp.legflange != null)
      s += approx(qs.legflange, isp.legflange) ? 80 : -45;
    const fields = ["type", "depth", "gauge"].filter((k) => qs[k] != null);
    if (
      fields.length &&
      fields.every(
        (k) =>
          isp[k] != null &&
          (k === "depth" ? approx(qs[k], isp[k]) : qs[k] === isp[k]),
      )
    )
      s += 60;
  }

  // Item code exact / prefix
  const cn = norm(item.itemCode);
  const qn = norm(rawQuery);
  if (cn && qn === cn) s += 150;
  else if (cn && qn.length >= 3 && cn.startsWith(qn)) s += 80;

  // Token overlap
  const hay = norm(
    [
      item.itemCode,
      item.description,
      item.costCodeDescription,
      item.itemDescription,
      item.size,
      item.uom,
      item.sectionNumber,
    ]
      .filter(Boolean)
      .join(" "),
  );
  const hayTok = new Set(searchTokens(hay));
  for (const qt of new Set(searchTokens(rawQuery))) {
    if (/^\d+$/.test(qt)) {
      if (!framing && hayTok.has(qt)) s += 8;
      continue;
    }
    if (/^\d{1,2}ga$/.test(qt)) continue;
    if (hayTok.has(qt)) s += 18;
    else if ((SEARCH_ALIASES[qt] || []).some((a) => hayTok.has(a))) s += 14;
    else if (qt.length >= 3 && hay.includes(qt)) s += 4;
  }
  return s;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── PDF export helpers (unchanged) ─────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
const blobToBase64 = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

const base64Regex = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
const validBase64 =
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

function base64Parser(tagValue) {
  if (typeof tagValue !== "string" || !base64Regex.test(tagValue)) return null;
  const stringBase64 = tagValue.replace(base64Regex, "");
  if (!validBase64.test(stringBase64)) throw new Error("Invalid base64 data");
  const binaryString = window.atob(stringBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++)
    bytes[i] = binaryString.charCodeAt(i);
  return bytes.buffer;
}

async function fetchAsBase64(url) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    return blobToBase64(await res.blob());
  } catch {
    return null;
  }
}

async function getImgDims(base64, maxW, maxH) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { naturalWidth: w, naturalHeight: h } = img;
      const scaleW = maxW / w,
        scaleH = maxH / h;
      const scale = Math.min(1, scaleW, scaleH);
      resolve({ w: Math.round(w * scale), h: Math.round(h * scale) });
    };
    img.onerror = () => resolve({ w: maxW, h: maxH });
    img.src = base64;
  });
}

export async function mergePDFBlobs(pdfBlobs) {
  const { PDFDocument } = await import("pdf-lib");
  const merged = await PDFDocument.create();
  for (const blob of pdfBlobs) {
    const arrayBuffer = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer);
    const pages = await merged.copyPages(pdf, pdf.getPageIndices());
    pages.forEach((page) => merged.addPage(page));
  }
  const mergedBytes = await merged.save();
  return new Blob([mergedBytes], { type: "application/pdf" });
}

const fmtDate = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
  ).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export async function generateScopePDF(
  tandm,
  scope,
  project,
  companyProfile,
  entryNumber = null,
) {
  try {
    const tmplRes = await fetch("/t_and_m_template.docx");
    if (!tmplRes.ok)
      throw new Error(
        "Could not load T&M template from /t_and_m_template.docx",
      );
    const templateArrayBuffer = await tmplRes.arrayBuffer();
    const PizZip = (await import("pizzip")).default;
    const Docxtemplater = (await import("docxtemplater")).default;
    const ImageModule = (await import("docxtemplater-image-module-free"))
      .default;

    let companyLogoBase64 = null;
    let logoDims = { w: 150, h: 60 };
    if (companyProfile?.logo) {
      companyLogoBase64 = companyProfile.logo.startsWith("data:image")
        ? companyProfile.logo
        : await fetchAsBase64(companyProfile.logo);
      if (companyLogoBase64)
        logoDims = await getImgDims(companyLogoBase64, 160, 70);
    }

    let signatureBase64 = null;
    let sigDims = { w: 0, h: 0 };
    if (scope.signature) {
      signatureBase64 = await fetchAsBase64(scope.signature);
      if (signatureBase64)
        sigDims = await getImgDims(signatureBase64, 230, 100);
    }

    // Only this scope's images, de-duplicated
    const imageUrls = [...new Set((scope.images || []).filter(Boolean))];
    const resolvedImages = [];
    for (const url of imageUrls) {
      const b64 = await fetchAsBase64(url);
      if (b64) resolvedImages.push({ image: b64 });
    }

    const zip = new PizZip(templateArrayBuffer);
    const imageModule = new ImageModule({
      getImage: (tagValue) => base64Parser(tagValue),
      getSize: (_img, _val, tagName) => {
        if (tagName === "companyLogo") return [logoDims.w, logoDims.h];
        if (tagName === "signature")
          return sigDims.w > 0 ? [sigDims.w, sigDims.h] : [1, 1];
        if (tagName === "image") return [220, 160];
        return [120, 60];
      },
    });
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      modules: [imageModule],
    });

    const lItems = (scope.items || []).filter((i) => i.category === "L");
    const mItems = (scope.items || []).filter((i) => i.category === "M");
    const items = [...lItems, ...mItems].map((item, i) => {
      const description =
        item.category === "M"
          ? [
              item.description || "Material",
              item.size && `(${item.size})`,
              item.notes && `— ${item.notes}`,
            ]
              .filter(Boolean)
              .join(" ")
          : [item.description || "Labour", item.notes && `— ${item.notes}`]
              .filter(Boolean)
              .join(" ");
      const isCov = item.category === "M" && isCoverageBased(item.uom || "");
      return {
        itemNo: i + 1,
        description,
        quantity: item.quantity || 0,
        unit: isCov ? "PC" : item.uom || "EA",
        price: "",
        amount: "",
      };
    });

    const companyAddress = companyProfile?.address
      ? [
          companyProfile.address.street,
          companyProfile.address.city,
          companyProfile.address.state,
          companyProfile.address.zip,
        ]
          .filter(Boolean)
          .join(", ")
      : "";

    const scopeNum =
      scope.scopeNumber != null
        ? String(scope.scopeNumber).padStart(2, "0")
        : null;

    const tmLabel = scopeNum
      ? `T&M #${tandm.timeAndMaterialNumber} - Entry ${scopeNum}`
      : `T&M #${tandm.timeAndMaterialNumber}`;

    // Entry's own description (name + note), fallback to T&M header text
    const entryDescription =
      [scope.name, scope.note].filter(Boolean).join(" — ") ||
      tandm.scopeOfWork ||
      "";

    const preparedBy =
      tandm.preparedBy?.displayName || tandm.createdBy?.displayName || "—";

    const data = {
      companyName: companyProfile?.companyName || "Toronto Tapers Inc.",
      companyAddress,
      companyLogo: companyLogoBase64,
      projectName: project?.projectName || "Unknown Project",
      date: fmtDate(tandm.date) || fmtDate(new Date().toISOString()),
      createdAt: fmtDate(scope.createdAt) || fmtDate(new Date().toISOString()),
      workCompletedDate: scope.workCompletedDate
        ? fmtDate(scope.workCompletedDate)
        : "—",
      timeAndMaterialNumber: tmLabel,
      scopeOfWork: entryDescription,
      preparedBy,
      signedBy: scope.signedBy || "—",
      signature: signatureBase64,
      items,
      hasImages: resolvedImages.length > 0, // boolean — prevents N² loop
      images: resolvedImages,
    };

    doc.setData(data);
    doc.render();

    const docxBlob = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    const token = localStorage.getItem("token");
    const safeScope = (scope.name || "scope")
      .replace(/[^a-z0-9]/gi, "_")
      .slice(0, 40);
    const filename = scopeNum
      ? `TandM_${tandm.timeAndMaterialNumber}_Entry${scopeNum}_${safeScope}`
      : `TandM_${tandm.timeAndMaterialNumber}_${safeScope}`;

    const formData = new FormData();
    formData.append("docx", docxBlob, `${filename}.docx`);
    formData.append("documentType", "TandM");
    formData.append("identifier", scope._id || "scope");

    const pdfRes = await axios.post(`${API}/upload/convert-to-pdf`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      responseType: "blob",
    });
    if (pdfRes.status !== 200)
      throw new Error("Server returned non-200 status from convert-to-pdf");

    return {
      blob: new Blob([pdfRes.data], { type: "application/pdf" }),
      filename,
    };
  } catch (err) {
    console.error("[generateScopePDF]", err);
    throw err;
  }
}

// ─── Signature Pad ────────────────────────────────────────────────────────────
// ─── Signature Pad ────────────────────────────────────────────────────────────
function SignaturePad({ onCapture, onClear, captured, existingUrl = null }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const last = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [showCanvas, setShowCanvas] = useState(!existingUrl);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const sx = c.width / r.width,
      sy = c.height / r.height;
    const src = e.touches?.[0] || e;
    return { x: (src.clientX - r.left) * sx, y: (src.clientY - r.top) * sy };
  };
  const start = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    last.current = pos(e);
  };
  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.stroke();
    last.current = p;
    if (!hasDrawn) setHasDrawn(true);
  };
  const stop = (e) => {
    e.preventDefault();
    isDrawing.current = false;
  };

  const clear = () => {
    const c = canvasRef.current;
    c?.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
    onClear?.();
  };

  const capture = () =>
    canvasRef.current?.toBlob((b) => b && onCapture(b), "image/png");

  // If we have an existing URL and haven't switched to redraw mode
  if (!showCanvas && existingUrl) {
    return (
      <div className="space-y-2">
        {/* Existing sig preview */}
        <div className="rounded-xl border border-green-700 bg-[#052e16] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-green-900/30 border-b border-green-800/50">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
            <span className="text-xs font-bold text-green-400 uppercase tracking-wider">
              Customer Signature on File
            </span>
          </div>
          <div className="p-3">
            <div className="bg-white rounded-lg p-2 inline-block">
              <img
                src={existingUrl}
                alt="Customer signature"
                className="max-h-16 max-w-xs block"
              />
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setShowCanvas(true);
            clear();
          }}
          className="px-3 py-1.5 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg border border-gray-600 transition-colors"
        >
          ✎ Redraw Signature
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Canvas area */}
      <div className="rounded-xl border-2 border-dashed border-gray-600 bg-white overflow-hidden relative">
        {/* Corner marks */}
        {[
          "top-1.5 left-1.5 border-t-2 border-l-2 rounded-tl",
          "top-1.5 right-1.5 border-t-2 border-r-2 rounded-tr",
          "bottom-1.5 left-1.5 border-b-2 border-l-2 rounded-bl",
          "bottom-1.5 right-1.5 border-b-2 border-r-2 rounded-br",
        ].map((c) => (
          <div
            key={c}
            className={cls(
              "absolute w-3 h-3 border-gray-300 pointer-events-none",
              c,
            )}
          />
        ))}
        {!hasDrawn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
            <span className="text-xs text-gray-400 font-medium">
              Draw signature here
            </span>
          </div>
        )}
        <canvas
          ref={canvasRef}
          width={520}
          height={130}
          className="w-full touch-none block"
          style={{ cursor: "crosshair" }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
        />
      </div>

      {/* Action row */}
      <div className="flex items-center gap-2">
        {/* Clear */}
        <button
          type="button"
          onClick={clear}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-950 hover:bg-red-900 text-red-400 rounded-lg border border-red-800 transition-colors"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14H6L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
          Clear
        </button>

        {/* Confirm */}
        <button
          type="button"
          onClick={capture}
          disabled={!hasDrawn}
          className={cls(
            "flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg transition-colors",
            hasDrawn
              ? "bg-green-700 hover:bg-green-600 text-white"
              : "bg-gray-700 text-gray-500 cursor-not-allowed border border-gray-600",
          )}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {hasDrawn ? "Confirm Signature" : "Draw to confirm"}
        </button>

        {captured && (
          <span className="flex items-center gap-1.5 text-xs text-green-400 font-semibold">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
            Captured
          </span>
        )}

        {/* Cancel redraw (only when switching from existing) */}
        {existingUrl && showCanvas && (
          <button
            type="button"
            onClick={() => {
              setShowCanvas(false);
              clear();
            }}
            className="ml-auto px-3 py-1.5 text-xs text-gray-500 hover:text-gray-300 rounded-lg border border-gray-700 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Scope form ───────────────────────────────────────────────────────────────
function ScopeForm({
  projectId,
  token,
  labourItems,
  initialScope = null,
  tandmId,
  onSaved,
  onCancel,
}) {
  const isNew = !initialScope;
  const [name, setName] = useState(initialScope?.name || "");
  const [note, setNote] = useState(initialScope?.note || "");
  const [signedBy, setSignedBy] = useState(initialScope?.signedBy || "");
  const [sigBlob, setSigBlob] = useState(null);
  const [sigCaptured, setSigCaptured] = useState(false);
  const [items, setItems] = useState(
    initialScope?.items?.map((it, i) => ({ ...it, _key: it._id || `k${i}` })) ||
      [],
  );
  const [showMat, setShowMat] = useState(false);
  const [showLabour, setShowLabour] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const fileRef = useRef(null);

  const addItem = (item) =>
    setItems((p) => [...p, { ...item, _key: `k${Date.now()}` }]);
  const remove = (idx) => setItems((p) => p.filter((_, i) => i !== idx));
  const updateQty = (idx, v) =>
    setItems((p) =>
      p.map((it, i) =>
        i === idx ? { ...it, quantity: parseFloat(v) || 0 } : it,
      ),
    );

  // Add with the other useState declarations at the top of ScopeForm:
  const [workCompletedDate, setWorkCompletedDate] = useState(
    initialScope?.workCompletedDate
      ? initialScope.workCompletedDate.split("T")[0]
      : "",
  );
  const updateNotes = (idx, v) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, notes: v } : it)));
  const updateSize = (idx, v) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, size: v } : it)));

  const mCount = items.filter((i) => i.category === "M").length;
  const lCount = items.filter((i) => i.category === "L").length;

  const handleSave = async () => {
    if (!name.trim()) return setError("Scope name is required.");
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("note", note);
      fd.append("signedBy", signedBy);
      fd.append("workCompletedDate", workCompletedDate || "");

      fd.append(
        "itemsJson",
        JSON.stringify(items.map(({ _key, isCoverage, ...it }) => it)),
      );
      // Only upload if a new blob was drawn — skip if we only have an existing URL
      if (sigBlob) fd.append("signature", sigBlob, "signature.png");
      images.forEach((f) => fd.append("images", f));

      const url = isNew
        ? `${API}/tandm3/${tandmId}/scopes`
        : `${API}/tandm3/${tandmId}/scopes/${initialScope._id}`;
      const res = await (isNew ? axios.post : axios.put)(url, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSaved(res.data.tandm);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {showMat && (
        <UnifiedItemSearch
          projectId={projectId}
          token={token}
          closeOnSelect={false}
          onSelect={(raw, qty) => {
            const isCov = ["SF", "LF"].some((u) =>
              (raw.uom || "").trim().toUpperCase().includes(u),
            );
            addItem({
              category: "M",
              budgetItemId: raw._isBudget ? raw._id || null : null,
              itemCode: raw.itemCode || "",
              description:
                raw.costCodeDescription ||
                raw.itemDescription ||
                raw.description ||
                "",
              size: raw.size || "",
              uom: raw.uom || "",
              sectionNumber: raw.sectionNumber || "",
              bidArea: raw.bidArea || "",
              quantity: qty || 1,
              notes: "",
              isCoverage: isCov,
            });
            setShowMat(false);
          }}
          onClose={() => setShowMat(false)}
          title="Add Material Item"
        />
      )}
      {showLabour && (
        <LabourItemPicker
          labourItems={labourItems}
          onAdd={(item) => {
            addItem(item);
            setShowLabour(false);
          }}
          onClose={() => setShowLabour(false)}
        />
      )}

      {error && (
        <div className="px-3.5 py-2.5 bg-red-900 border border-red-700 rounded-lg text-xs text-red-300">
          {error}
        </div>
      )}

      {/* Name + Note */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Scope Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Extra drywall — 3rd floor east"
            className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Work Completed Date
          </label>
          <input
            type="date"
            value={workCompletedDate}
            onChange={(e) => setWorkCompletedDate(e.target.value)}
            className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Note
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional details"
            className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Items
            </span>
            {mCount > 0 && (
              <span className="text-xs px-2 py-0.5 bg-blue-900 text-blue-300 border border-blue-700 rounded-full font-medium">
                {mCount}M
              </span>
            )}
            {lCount > 0 && (
              <span className="text-xs px-2 py-0.5 bg-yellow-900 text-yellow-300 border border-yellow-700 rounded-full font-medium">
                {lCount}L
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowMat(true)}
              className="px-3 py-1.5 text-xs font-bold bg-blue-700 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              + Material
            </button>
            <button
              type="button"
              onClick={() => setShowLabour(true)}
              className="px-3 py-1.5 text-xs font-bold bg-yellow-700 hover:bg-yellow-600 text-white rounded-lg transition-colors"
            >
              + Labour
            </button>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="border-2 border-dashed border-gray-700 rounded-lg py-8 text-center">
            <p className="text-sm text-gray-500">
              No items yet — add materials or labour above.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {items.map((item, idx) => (
              <ItemRow
                key={item._key || idx}
                item={item}
                idx={idx}
                onUpdateQty={updateQty}
                onUpdateNotes={updateNotes}
                onUpdateSize={updateSize}
                onRemove={remove}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Customer Sign-Off ── */}
      <div className="rounded-xl border border-gray-700 bg-gray-900 p-4 space-y-3">
        {/* Section header */}
        <div className="flex items-center gap-2 pb-2 border-b border-gray-700">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
            <polyline points="16 11 18 13 22 9" />
          </svg>
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
            Customer Sign-Off
          </span>
          <span className="text-xs text-gray-600 normal-case font-normal ml-1">
            — optional, captured in field
          </span>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
            Signed By
          </label>
          <input
            type="text"
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
            placeholder="Customer or site representative name"
            className="w-full px-3 py-2.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
            Signature
          </label>
          <SignaturePad
            existingUrl={initialScope?.signature || null}
            captured={sigCaptured}
            onCapture={(b) => {
              setSigBlob(b);
              setSigCaptured(true);
            }}
            onClear={() => {
              setSigBlob(null);
              setSigCaptured(false);
            }}
          />
        </div>
      </div>

      {/* Photos */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="px-3 py-1.5 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg border border-gray-600 transition-colors"
        >
          📷 Attach Photos
        </button>
        {images.length > 0 && (
          <span className="text-xs text-green-400 font-medium">
            {images.length} photo(s) selected
          </span>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) =>
            setImages((p) => [...p, ...Array.from(e.target.files)])
          }
        />
      </div>

      {/* Save/Cancel */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
        <button
          onClick={handleSave}
          disabled={saving}
          className={cls(
            "px-5 py-2 text-sm font-bold rounded-lg text-white transition-colors",
            saving
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600",
          )}
        >
          {saving ? "Saving…" : isNew ? "Add Scope" : "Save Changes"}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Scope approval panel ─────────────────────────────────────────────────────
function ScopeApprovalPanel({ tandmId, scope, token, onSaved, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  const submit = async (status) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("status", status);
      if (status === "Rejected") fd.append("rejectedReason", rejectReason);
      const res = await axios.patch(
        `${API}/tandm3/${tandmId}/scopes/${scope._id}/status`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved(res.data.tandm);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Action Failed",
        message: err.response?.data?.message || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const hasCustomerSig = !!(scope.signature || scope.signedBy);

  return (
    <div className="bg-gray-900 border border-green-700 rounded-xl p-4 space-y-4">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText="OK"
        cancelText="Dismiss"
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        onConfirm={() => setAlert((a) => ({ ...a, open: false }))}
      />

      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-green-800 border border-green-600 flex items-center justify-center text-green-300 text-xs font-bold shrink-0">
          ✓
        </div>
        <h4 className="text-sm font-bold text-white">Approve: {scope.name}</h4>
      </div>

      {hasCustomerSig ? (
        <div className="rounded-xl border border-green-800 bg-[#052e16] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-green-900/40 border-b border-green-800/50">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#34d399"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
              <polyline points="16 11 18 13 22 9" />
            </svg>
            <span className="text-xs font-bold text-green-400 uppercase tracking-wider">
              Customer Sign-Off on File
            </span>
          </div>
          <div className="px-3 py-2.5 space-y-2.5">
            {scope.signedBy && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">Signed by:</span>
                <span className="text-gray-200 font-semibold">
                  {scope.signedBy}
                </span>
              </div>
            )}
            {scope.signature && (
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Signature</p>
                <div className="bg-white rounded-lg p-2 inline-block border border-gray-200">
                  <img
                    src={scope.signature}
                    alt="Customer signature"
                    className="max-h-16 max-w-xs block"
                  />
                </div>
                <div className="mt-1">
                  <a
                    href={scope.signature}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                  >
                    Open full size ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2.5 px-3.5 py-3 bg-gray-800 border border-gray-700 rounded-lg">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mt-0.5 shrink-0"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-xs text-gray-500">
            No customer signature was collected in the field for this scope. You
            can still approve if the work has been verified by other means.
          </p>
        </div>
      )}

      <p className="text-xs text-gray-500 leading-relaxed">
        Review the scope items{hasCustomerSig ? " and customer sign-off" : ""}{" "}
        above, then approve or reject.
      </p>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-700">
        <button
          onClick={() => submit("Approved")}
          disabled={saving}
          className={cls(
            "flex items-center gap-1.5 px-5 py-2 text-sm font-bold rounded-lg text-white transition-colors",
            saving
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-green-700 hover:bg-green-600",
          )}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {saving && !showReject ? "Saving…" : "Approve Scope"}
        </button>
        <button
          onClick={() => setShowReject((r) => !r)}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 rounded-lg border border-red-800 hover:border-red-600 transition-colors"
        >
          Reject
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>

      {showReject && (
        <div className="space-y-2 bg-red-950 border border-red-800 rounded-lg p-3">
          <input
            type="text"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection…"
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:border-red-500 focus:outline-none"
          />
          <button
            onClick={() => submit("Rejected")}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            {saving ? "…" : "Confirm Reject"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Scope card ───────────────────────────────────────────────────────────────
function ScopeCard({
  scope,
  tandmId,
  projectId,
  token,
  labourItems,
  onTandMUpdated,
  tandm,
  tandms, // ← add this
  project,
  companyProfile,
}) {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [approveMode, setApproveMode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    mode: "alert",
  });

  const closeAlert = () => setAlert((a) => ({ ...a, open: false }));

  const canEdit = scope.status !== "Approved" && !scope.convertedToChangeOrder;
  const canApprove =
    scope.status === "Pending" && !scope.convertedToChangeOrder;
  const canDelete =
    scope.status !== "Approved" && !scope.convertedToChangeOrder;
  const canRevert =
    scope.status === "Approved" && !scope.convertedToChangeOrder;
  const isApproved = scope.status === "Approved";
  const bar = STATUS[scope.status]?.bar || "bg-gray-500";
  const mCount = (scope.items || []).filter((i) => i.category === "M").length;
  const lCount = (scope.items || []).filter((i) => i.category === "L").length;

  // Called when user confirms the delete dialog
  const executeDelete = async () => {
    setDeleting(true);
    closeAlert();
    try {
      const res = await axios.delete(
        `${API}/tandm3/${tandmId}/scopes/${scope._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onTandMUpdated(res.data.tandm);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Delete Failed",
        message: err.response?.data?.message || err.message,
        mode: "alert",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    setAlert({
      open: true,
      type: "error",
      title: "Delete Scope",
      message: `Are you sure you want to delete "${scope.name}"? This cannot be undone.`,
      mode: "confirm-delete",
    });
  };

  const handleRevert = async () => {
    setReverting(true);
    closeAlert();
    setConfirmRevert(false);
    try {
      const fd = new FormData();
      fd.append("status", "Pending");
      const res = await axios.patch(
        `${API}/tandm3/${tandmId}/scopes/${scope._id}/status`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onTandMUpdated(res.data.tandm);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Revert Failed",
        message: err.response?.data?.message || err.message,
        mode: "alert",
      });
    } finally {
      setReverting(false);
    }
  };

  const handleRevertClick = () => {
    setAlert({
      open: true,
      type: "error",
      title: "Revert to Pending",
      message: `Revert "${scope.name}" back to Pending? This will clear the signature and approval date.${tandm?.status === "Approved" ? " The overall T&M will also revert to Pending." : ""}`,
      mode: "confirm-revert",
    });
  };

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 overflow-hidden">
      {/* ── Alert / confirm dialog ── */}
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={
          alert.mode === "confirm-delete"
            ? "Delete"
            : alert.mode === "confirm-revert"
              ? "Yes, Revert"
              : "OK"
        }
        cancelText={alert.mode === "alert" ? "Dismiss" : "Cancel"}
        onClose={closeAlert}
        onConfirm={() => {
          if (alert.mode === "confirm-delete") executeDelete();
          else if (alert.mode === "confirm-revert") handleRevert();
          else closeAlert();
        }}
      />

      <div className="flex">
        <div className={cls("w-1.5 shrink-0", bar)} />
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div
            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-700 transition-colors"
            onClick={() => !editMode && !approveMode && setExpanded((e) => !e)}
          >
            <span className="text-gray-500 text-xs shrink-0 select-none">
              {expanded ? "▾" : "▸"}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Entry number badge */}
                {scope.scopeNumber != null && (
                  <span className="text-xs font-mono font-bold px-2 py-0.5 bg-gray-700 text-gray-300 border border-gray-600 rounded shrink-0">
                    #{String(scope.scopeNumber).padStart(2, "0")}
                  </span>
                )}
                <p className="text-sm font-bold text-white">{scope.name}</p>
                <StatusPill status={scope.status} size="xs" />
                {scope.convertedToChangeOrder && (
                  <span className="text-xs font-bold px-2 py-0.5 bg-purple-900 text-purple-300 border border-purple-700 rounded-full">
                    Converted
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                {scope.note && (
                  <p className="text-xs text-gray-500 truncate">{scope.note}</p>
                )}
                {scope.workCompletedDate && (
                  <span className="text-xs text-blue-400 font-medium shrink-0">
                    🗓 {fmtDate(scope.workCompletedDate)}
                  </span>
                )}
              </div>
            </div>
            <div
              className="flex items-center gap-2 shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              {(mCount > 0 || lCount > 0) && (
                <div className="flex items-center gap-1.5">
                  {mCount > 0 && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-900 text-blue-300 border border-blue-800 rounded font-medium">
                      {mCount}M
                    </span>
                  )}
                  {lCount > 0 && (
                    <span className="text-xs px-1.5 py-0.5 bg-yellow-900 text-yellow-300 border border-yellow-800 rounded font-medium">
                      {lCount}L
                    </span>
                  )}
                </div>
              )}
              {(scope.items || []).length > 0 && (
                <button
                  onClick={async () => {
                    setExporting(true);
                    setExportError("");
                    try {
                      const result = await generateScopePDF(
                        tandm,
                        scope,
                        project,
                        companyProfile,
                      );
                      const url = URL.createObjectURL(result.blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${result.filename}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      setTimeout(() => URL.revokeObjectURL(url), 150);
                    } catch (err) {
                      setAlert({
                        open: true,
                        type: "error",
                        title: "PDF Export Failed",
                        message: err.message || "Could not generate PDF.",
                        mode: "alert",
                      });
                    } finally {
                      setExporting(false);
                    }
                  }}
                  disabled={exporting}
                  title={
                    isApproved
                      ? "Export signed PDF"
                      : "Export a PDF now (e.g. to get it signed in the field)"
                  }
                  className={cls(
                    "px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors",
                    exporting
                      ? "bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
                      : isApproved
                        ? "bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600"
                        : "bg-blue-950 hover:bg-blue-900 text-blue-300 border-blue-800",
                  )}
                >
                  {exporting
                    ? "Generating…"
                    : isApproved
                      ? "⬇ PDF"
                      : "⬇ PDF (unsigned)"}
                </button>
              )}
              {canRevert && (
                <button
                  onClick={handleRevertClick}
                  disabled={reverting}
                  className="px-2.5 py-1 text-xs font-medium bg-gray-700 hover:bg-yellow-900 text-gray-400 hover:text-yellow-300 rounded-lg border border-gray-600 hover:border-yellow-700 transition-colors"
                >
                  ↩ Revert
                </button>
              )}
              {canApprove && !approveMode && !editMode && (
                <button
                  onClick={() => {
                    setApproveMode(true);
                    setEditMode(false);
                    setExpanded(true);
                  }}
                  className="px-2.5 py-1 text-xs font-bold bg-green-700 hover:bg-green-600 text-white rounded-lg transition-colors"
                >
                  ✓ Approve
                </button>
              )}
              {canEdit && !editMode && (
                <button
                  onClick={() => {
                    setEditMode(true);
                    setApproveMode(false);
                    setExpanded(true);
                  }}
                  className="px-2.5 py-1 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg border border-gray-600 transition-colors"
                >
                  ✎ Edit
                </button>
              )}
              {/* ── Cancel edit button — shown only while editing ── */}
              {editMode && (
                <button
                  onClick={() => {
                    setEditMode(false);
                  }}
                  className="px-2.5 py-1 text-xs font-medium bg-gray-700 hover:bg-red-900 text-gray-400 hover:text-red-300 rounded-lg border border-gray-600 hover:border-red-700 transition-colors"
                >
                  ✕ Cancel
                </button>
              )}
              {canDelete && (
                <button
                  onClick={handleDeleteClick}
                  disabled={deleting}
                  className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-red-400 rounded-lg transition-colors"
                  title="Delete scope"
                >
                  {deleting ? (
                    <span className="text-xs">…</span>
                  ) : (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>

          {expanded && (
            <div className="px-4 pb-4 border-t border-gray-700 space-y-4">
              {editMode ? (
                <div className="mt-4">
                  <ScopeForm
                    projectId={projectId}
                    token={token}
                    labourItems={labourItems}
                    initialScope={scope}
                    tandmId={tandmId}
                    onSaved={(t) => {
                      setEditMode(false);
                      onTandMUpdated(t);
                    }}
                    onCancel={() => setEditMode(false)}
                  />
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  {["M", "L"].map((cat) => {
                    const catItems = (scope.items || []).filter(
                      (i) => i.category === cat,
                    );
                    if (!catItems.length) return null;
                    const isMatCat = cat === "M";
                    return (
                      <div key={cat}>
                        <p
                          className={cls(
                            "text-xs font-bold uppercase tracking-wider mb-1.5",
                            isMatCat ? "text-blue-400" : "text-yellow-400",
                          )}
                        >
                          {isMatCat ? "Materials" : "Labour"}
                        </p>
                        <div className="rounded-lg border border-gray-700 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-900">
                              <tr className="text-gray-500">
                                <th className="px-3 py-2 text-left font-medium">
                                  Code
                                </th>
                                <th className="px-3 py-2 text-left font-medium min-w-[120px]">
                                  Description
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                  Size
                                </th>
                                <th className="px-3 py-2 text-right font-medium">
                                  Qty
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                  Unit
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                  Coverage
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                  Notes
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {catItems.map((item, i) => {
                                const itemUom = item.uom || "";
                                const isCov =
                                  isMatCat && isCoverageBased(itemUom);
                                const cov = isCov
                                  ? parseSizeCoverage(item.size || "", itemUom)
                                  : null;
                                return (
                                  <tr
                                    key={item._id || i}
                                    className={
                                      i % 2 === 0
                                        ? "bg-gray-800"
                                        : "bg-gray-850"
                                    }
                                  >
                                    <td className="px-3 py-2">
                                      <span
                                        className={cls(
                                          "font-mono font-bold text-xs px-1.5 py-0.5 rounded",
                                          isMatCat
                                            ? "bg-blue-900 text-blue-300"
                                            : "bg-yellow-900 text-yellow-300",
                                        )}
                                      >
                                        {item.itemCode || "—"}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-gray-200">
                                      {item.description || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-400 font-mono">
                                      {item.size || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-200 font-mono font-bold">
                                      {item.quantity}
                                    </td>
                                    <td className="px-3 py-2 text-gray-400">
                                      {isCov ? "PC" : itemUom || "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                      {isCov && cov !== null ? (
                                        <span className="text-green-400 font-mono font-bold">
                                          {cov} {itemUom}
                                        </span>
                                      ) : isCov && item.size ? (
                                        <span className="text-yellow-500 text-[10px]">
                                          ⚠ unreadable
                                        </span>
                                      ) : (
                                        <span className="text-gray-600">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-gray-500">
                                      {item.notes || ""}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  {!(scope.items || []).length && (
                    <p className="text-xs text-gray-600 italic py-2">
                      No items added yet.
                    </p>
                  )}
                </div>
              )}

              {approveMode && !editMode && (
                <ScopeApprovalPanel
                  tandmId={tandmId}
                  scope={scope}
                  token={token}
                  onSaved={(t) => {
                    setApproveMode(false);
                    onTandMUpdated(t);
                  }}
                  onCancel={() => setApproveMode(false)}
                />
              )}

              {isApproved && (
                <div className="bg-gray-900 border border-green-800 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-green-400 uppercase tracking-wider">
                    Approval Details
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500 mb-0.5">Signed By</p>
                      <p className="text-gray-200 font-medium">
                        {scope.signedBy || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-0.5">Date Approved</p>
                      <p className="text-gray-200">
                        {scope.approvedAt
                          ? new Date(scope.approvedAt).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-0.5">Work Completed</p>
                      <p className="text-gray-200">
                        {scope.workCompletedDate
                          ? fmtDate(scope.workCompletedDate)
                          : "—"}
                      </p>
                    </div>
                    {(scope.images || []).length > 0 && (
                      <div>
                        <p className="text-gray-500 mb-0.5">Site Photos</p>
                        <p className="text-green-400 font-medium">
                          {scope.images.length} attached
                        </p>
                      </div>
                    )}
                  </div>
                  {scope.signature && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">Signature</p>
                      <div className="bg-white rounded border border-gray-300 p-1.5 inline-block">
                        <img
                          src={scope.signature}
                          alt="Signature"
                          className="max-h-16 max-w-xs"
                        />
                      </div>
                      <div className="mt-1">
                        <a
                          href={scope.signature}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                        >
                          Open full size ↗
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {scope.status === "Rejected" && scope.rejectedReason && (
                <div className="bg-red-950 border border-red-800 rounded-lg px-3 py-2 text-xs text-red-300">
                  <strong>Rejection reason:</strong> {scope.rejectedReason}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Labour picker modal (unchanged) ─────────────────────────────────────────
function LabourItemPicker({ labourItems, onAdd, onClose }) {
  const [qty, setQty] = useState({});
  const getName = (li) =>
    li.title ||
    li.labourItem?.title ||
    li.labourItem?.itemCode ||
    li.itemCode ||
    "Labour Item";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.78)" }}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[82vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div>
            <h3 className="text-sm font-bold text-white">Add Labour Item</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Select from project labour rates
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white text-lg transition-colors"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1.5">
          {labourItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-4xl mb-3">👷</p>
              <p className="text-sm text-gray-400 font-medium">
                No labour rates for this project.
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Add rates in the Labour Rates tab first.
              </p>
            </div>
          )}
          {labourItems.map((li) => {
            const name = getName(li);
            const code = li.itemCode || li.labourItem?.itemCode || "L";
            return (
              <div
                key={li._id}
                className="flex items-center gap-3 bg-gray-800 rounded-lg px-4 py-3 border border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono font-bold text-yellow-400 bg-gray-900 px-1.5 py-0.5 rounded border border-gray-700">
                      {code}
                    </span>
                    <span className="text-xs text-gray-500">{li.uom}</span>
                  </div>
                  <p className="text-sm text-gray-200 font-medium">{name}</p>
                  <div className="flex gap-4 mt-0.5">
                    <span className="text-xs text-gray-500">
                      Rate:{" "}
                      <span className="text-gray-300 font-mono">
                        ${(li.unitRate || 0).toFixed(2)}/{li.uom}
                      </span>
                    </span>
                    <span className="text-xs text-gray-500">
                      Cost:{" "}
                      <span className="text-gray-300 font-mono">
                        ${(li.unitCost || 0).toFixed(2)}/{li.uom}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="number"
                    value={qty[li._id] ?? ""}
                    min="0"
                    placeholder="Qty"
                    onChange={(e) =>
                      setQty((p) => ({ ...p, [li._id]: e.target.value }))
                    }
                    className="w-20 px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-200 text-right focus:border-orange-500 focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      onAdd({
                        category: "L",
                        contractLabourItemId: li._id,
                        itemCode: code,
                        description: name,
                        uom: li.uom || "",
                        unitRate: li.unitRate || 0,
                        unitCost: li.unitCost || 0,
                        size: "",
                        sectionNumber: li.sectionNumber || "",
                        quantity: parseFloat(qty[li._id]) || 1,
                        notes: "",
                      });
                      setQty((p) => ({ ...p, [li._id]: "" }));
                    }}
                    className="px-3 py-1.5 text-xs font-bold bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
                  >
                    + Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── IMPROVED Item row — with inline size editing for LF/SF items ────────────
// ═══════════════════════════════════════════════════════════════════════════════
function ItemRow({
  item,
  idx,
  onUpdateQty,
  onUpdateNotes,
  onUpdateSize,
  onRemove,
}) {
  const isM = item.category === "M";
  const itemUom = item.uom || "";
  const isCov = isM && isCoverageBased(itemUom);
  const coverage = isCov ? parseSizeCoverage(item.size || "", itemUom) : null;
  const coverageOk = coverage !== null;
  const [showSizeEdit, setShowSizeEdit] = useState(false);

  return (
    <div
      className={cls(
        "bg-gray-900 rounded-lg px-3.5 py-2.5 border",
        isCov && !item.size ? "border-yellow-800" : "border-gray-700",
      )}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={cls(
            "text-xs font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5",
            isM
              ? "bg-blue-900 text-blue-300 border border-blue-700"
              : "bg-yellow-900 text-yellow-300 border border-yellow-700",
          )}
        >
          {item.category}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span
              className={cls(
                "text-xs font-mono font-bold",
                isM ? "text-blue-400" : "text-yellow-400",
              )}
            >
              {item.itemCode}
            </span>
            {isCov && (
              <span
                className={cls(
                  "text-[10px] font-bold px-1.5 py-0.5 rounded border",
                  coverageOk
                    ? "bg-green-900 text-green-300 border-green-800"
                    : "bg-yellow-900 text-yellow-300 border-yellow-800",
                )}
              >
                {coverageOk
                  ? `${item.size} = ${coverage} ${itemUom}`
                  : item.size
                    ? "⚠ can't parse size"
                    : "⚠ size needed"}
              </span>
            )}
            {!isCov && item.size && (
              <span className="text-xs text-gray-500">{item.size}</span>
            )}
          </div>
          <p className="text-sm text-gray-300 truncate">{item.description}</p>

          {/* Size edit for coverage-based items */}
          {isCov && (showSizeEdit || !item.size) && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1">
                <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">
                  {itemUom.toUpperCase().includes("SF")
                    ? "Dimensions (e.g. 4'×8')"
                    : "Length (e.g. 12' or 8'6\")"}
                </label>
                <input
                  type="text"
                  value={item.size || ""}
                  onChange={(e) => onUpdateSize(idx, e.target.value)}
                  placeholder={
                    itemUom.toUpperCase().includes("SF") ? "4'×8'" : "8'"
                  }
                  autoFocus={!item.size}
                  className={cls(
                    "w-full px-2.5 py-1.5 text-xs border rounded-lg text-gray-200 placeholder-gray-600 focus:outline-none font-mono",
                    item.size
                      ? coverageOk
                        ? "bg-gray-800 border-green-700 focus:border-green-500"
                        : "bg-gray-800 border-yellow-700 focus:border-yellow-500"
                      : "bg-gray-800 border-orange-700 focus:border-orange-500",
                  )}
                />
              </div>
              {item.size && coverageOk && (
                <div className="mt-5 shrink-0 px-2.5 py-1.5 rounded-lg bg-green-900 border border-green-700 text-xs font-bold text-green-300 whitespace-nowrap">
                  = {coverage} {itemUom}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Qty:</span>
              <input
                type="number"
                value={item.quantity}
                min="0"
                onChange={(e) => onUpdateQty(idx, e.target.value)}
                className="w-16 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 text-right focus:border-orange-500 focus:outline-none"
              />
              <span className="text-xs text-gray-500">
                {isCov ? "PC" : itemUom}
              </span>
            </div>
            <input
              type="text"
              value={item.notes || ""}
              placeholder="Notes…"
              onChange={(e) => onUpdateNotes(idx, e.target.value)}
              className="flex-1 px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-400 placeholder-gray-600 focus:border-orange-500 focus:outline-none min-w-[80px]"
            />
            {isCov && item.size && (
              <button
                onClick={() => setShowSizeEdit((v) => !v)}
                className={cls(
                  "px-2 py-1 text-[10px] font-bold rounded border transition-colors",
                  showSizeEdit
                    ? "bg-gray-700 border-gray-600 text-gray-400"
                    : "bg-blue-900 border-blue-700 text-blue-300 hover:bg-blue-800",
                )}
              >
                {showSizeEdit ? "Hide size" : "✎ Size"}
              </button>
            )}
          </div>
        </div>

        <button
          onClick={() => onRemove(idx)}
          className="w-6 h-6 flex items-center justify-center shrink-0 text-gray-600 hover:text-red-400 rounded transition-colors mt-0.5"
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ─── T&M create form ──────────────────────────────────────────────────────────
function TandMCreateForm({ projectId, token, onCreated, onCancel }) {
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [withScope, setWithScope] = useState(true);
  const [scopeName, setScopeName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!scopeOfWork.trim()) return setError("Scope of work is required.");
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("scopeOfWork", scopeOfWork.trim());
      fd.append("date", date);
      if (withScope && scopeName.trim())
        fd.append("scopeName", scopeName.trim());
      const res = await axios.post(`${API}/tandm3/project/${projectId}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated(res.data.tandm);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-white">New T&M Entry</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Create a time & material field ticket
          </p>
        </div>
        <button
          onClick={onCancel}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-white transition-colors text-lg"
        >
          ×
        </button>
      </div>
      {error && (
        <div className="px-3.5 py-2.5 bg-red-900 border border-red-700 rounded-lg text-xs text-red-300">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Scope of Work *
          </label>
          <input
            type="text"
            value={scopeOfWork}
            onChange={(e) => setScopeOfWork(e.target.value)}
            placeholder="e.g. Additional drywall — 3rd floor"
            autoFocus
            className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          />
        </div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <button
          type="button"
          onClick={() => setWithScope((v) => !v)}
          className={cls(
            "relative w-11 h-6 rounded-full transition-colors shrink-0",
            withScope ? "bg-orange-500" : "bg-gray-600",
          )}
        >
          <div
            className={cls(
              "absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform",
              withScope ? "translate-x-5" : "translate-x-0.5",
            )}
          />
        </button>
        <span className="text-sm text-gray-300">Add first scope now</span>
      </label>
      {withScope && (
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            First Scope Name
          </label>
          <input
            type="text"
            value={scopeName}
            onChange={(e) => setScopeName(e.target.value)}
            placeholder="e.g. East wing — extra partition walls"
            className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
          />
          <p className="text-xs text-gray-600 mt-1">
            Items are added to the scope after creation.
          </p>
        </div>
      )}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
        <button
          onClick={handleCreate}
          disabled={saving || !scopeOfWork.trim()}
          className={cls(
            "px-5 py-2 text-sm font-bold rounded-lg text-white transition-colors",
            saving || !scopeOfWork.trim()
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600",
          )}
        >
          {saving ? "Creating…" : "Create T&M"}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Export all approved scopes ───────────────────────────────────────────────
function ExportAllButton({ tandm, tandms, project, companyProfile }) {
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [errors, setErrors] = useState([]);

  // ExportAllButton — remove the entryNumber resolution entirely:
  const handleExportAll = async () => {
    const approved = (tandm.scopes || []).filter(
      (s) => s.status === "Approved",
    );
    if (!approved.length) return;

    // No more entryNumber needed — scopeNumber is on each scope
    setPending(true);
    setDone(0);
    setTotal(approved.length);
    setErrors([]);

    const blobs = [];
    const errs = [];

    for (const scope of approved) {
      try {
        const result = await generateScopePDF(
          tandm,
          scope,
          project,
          companyProfile,
        );
        blobs.push(result.blob);
      } catch (err) {
        errs.push(`${scope.name}: ${err.message}`);
      }
      setDone((p) => p + 1);
    }

    setErrors(errs);
    if (blobs.length === 0) {
      setPending(false);
      return;
    }

    try {
      const saveAs = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 150);
      };

      if (blobs.length === 1) {
        saveAs(
          blobs[0],
          `${
            approved[0].scopeNumber != null
              ? `TandM_${tandm.timeAndMaterialNumber}_Entry${String(approved[0].scopeNumber).padStart(2, "0")}`
              : `TandM_${tandm.timeAndMaterialNumber}`
          }_${approved[0].name.replace(/[^a-z0-9]/gi, "_").slice(0, 40)}.pdf`,
        );
      } else {
        const merged = await mergePDFBlobs(blobs);
        saveAs(merged, `TandM_${tandm.timeAndMaterialNumber}_all_scopes.pdf`);
      }
    } catch (mergeErr) {
      setErrors((p) => [...p, `Merge failed: ${mergeErr.message}`]);
    }

    setPending(false);
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleExportAll}
        disabled={pending}
        className={cls(
          "px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors",
          pending
            ? "bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
            : "bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600",
        )}
      >
        {pending ? `Exporting ${done}/${total}…` : "⬇ Export All PDFs"}
      </button>
      {errors.length > 0 && (
        <span className="text-xs text-red-400" title={errors.join("\n")}>
          {errors.length} failed
        </span>
      )}
    </div>
  );
}

// ─── T&M detail ───────────────────────────────────────────────────────────────
// ─── T&M detail ───────────────────────────────────────────────────────────────
function TandMDetail({
  tandm,
  projectId,
  token,
  labourItems,
  onUpdated,
  onBack,
  project,
  companyProfile,
  tandms,
}) {
  const [addingScope, setAddingScope] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [sigBlob, setSigBlob] = useState(null);
  const [sigCaptured, setSigCaptured] = useState(false);
  const [signedBy, setSignedBy] = useState(tandm.signedBy || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ── Header edit state ──
  const initialPreparedBy = tandm.preparedBy?._id || tandm.createdBy?._id || "";
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerScopeOfWork, setHeaderScopeOfWork] = useState(
    tandm.scopeOfWork || "",
  );
  const [headerDate, setHeaderDate] = useState(
    tandm.date
      ? tandm.date.split("T")[0]
      : new Date().toISOString().split("T")[0],
  );
  const [headerPreparedBy, setHeaderPreparedBy] = useState(initialPreparedBy);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState("");

  const preparedByName =
    tandm.preparedBy?.displayName || tandm.createdBy?.displayName || "—";

  // Load company users the first time the header editor opens
  useEffect(() => {
    if (!editingHeader || companyUsers.length) return;
    setUsersLoading(true);
    axios
      .get(`${API}/tandm3/company-users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setCompanyUsers(r.data.users || []))
      .catch(() => setCompanyUsers([]))
      .finally(() => setUsersLoading(false));
  }, [editingHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  const openHeaderEdit = () => {
    setHeaderScopeOfWork(tandm.scopeOfWork || "");
    setHeaderDate(
      tandm.date
        ? tandm.date.split("T")[0]
        : new Date().toISOString().split("T")[0],
    );
    setHeaderPreparedBy(tandm.preparedBy?._id || tandm.createdBy?._id || "");
    setHeaderError("");
    setEditingHeader(true);
  };

  const handleHeaderSave = async () => {
    if (!headerScopeOfWork.trim())
      return setHeaderError("Scope of work is required.");
    setHeaderSaving(true);
    setHeaderError("");
    try {
      const fd = new FormData();
      fd.append("scopeOfWork", headerScopeOfWork.trim());
      fd.append("date", headerDate);
      fd.append("preparedBy", headerPreparedBy || "");
      const res = await axios.put(`${API}/tandm3/${tandm._id}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onUpdated(res.data.tandm);
      setEditingHeader(false);
    } catch (err) {
      setHeaderError(err.response?.data?.message || err.message);
    } finally {
      setHeaderSaving(false);
    }
  };

  const handleCancelHeaderEdit = () => {
    setHeaderScopeOfWork(tandm.scopeOfWork || "");
    setHeaderDate(
      tandm.date
        ? tandm.date.split("T")[0]
        : new Date().toISOString().split("T")[0],
    );
    setHeaderPreparedBy(tandm.preparedBy?._id || tandm.createdBy?._id || "");
    setHeaderError("");
    setEditingHeader(false);
  };

  const approved = (tandm.scopes || []).filter((s) => s.status === "Approved");
  const unconverted = approved.filter((s) => !s.convertedToChangeOrder);
  const totalItems = (tandm.scopes || []).reduce(
    (n, s) => n + (s.items?.length || 0),
    0,
  );
  const allApproved =
    (tandm.scopes || []).length > 0 &&
    (tandm.scopes || []).every((s) => s.status === "Approved");

  const handleApproveTandM = async () => {
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("signedBy", signedBy);
      if (sigBlob) fd.append("signature", sigBlob, "signature.png");
      await axios.put(`${API}/tandm3/${tandm._id}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.patch(
        `${API}/tandm3/${tandm._id}/status`,
        { status: "Approved" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onUpdated(res.data.tandm);
      setApprovingAll(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const canEditHeader = tandm.status !== "Converted";

  const sortedScopes = [...(tandm.scopes || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );

  // Make sure the current preparer shows in the dropdown even if not in list yet
  const userOptions = useMemo(() => {
    const list = [...companyUsers];
    const current = tandm.preparedBy || tandm.createdBy;
    if (current?._id && !list.some((u) => u._id === current._id))
      list.unshift({ _id: current._id, displayName: current.displayName });
    return list;
  }, [companyUsers, tandm.preparedBy, tandm.createdBy]);

  return (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 px-5 py-4">
        {editingHeader ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-orange-400 uppercase tracking-wider">
                Editing T&M #{tandm.timeAndMaterialNumber}
              </span>
            </div>
            {headerError && (
              <div className="px-3 py-2 bg-red-900 border border-red-700 rounded-lg text-xs text-red-300">
                {headerError}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                  Scope of Work *
                </label>
                <input
                  type="text"
                  value={headerScopeOfWork}
                  onChange={(e) => setHeaderScopeOfWork(e.target.value)}
                  autoFocus
                  className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                  Date
                </label>
                <input
                  type="date"
                  value={headerDate}
                  onChange={(e) => setHeaderDate(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                  Prepared By
                </label>
                <select
                  value={headerPreparedBy}
                  onChange={(e) => setHeaderPreparedBy(e.target.value)}
                  disabled={usersLoading}
                  className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
                >
                  <option value="">
                    {usersLoading ? "Loading users…" : "— Select user —"}
                  </option>
                  {userOptions.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.displayName || u.email || "Unnamed user"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleHeaderSave}
                disabled={headerSaving || !headerScopeOfWork.trim()}
                className={cls(
                  "px-4 py-2 text-sm font-bold rounded-lg text-white transition-colors",
                  headerSaving || !headerScopeOfWork.trim()
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-orange-500 hover:bg-orange-600",
                )}
              >
                {headerSaving ? "Saving…" : "Save Changes"}
              </button>
              <button
                onClick={handleCancelHeaderEdit}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-700 hover:border-gray-500 transition-colors shrink-0"
            >
              ← Back
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="text-base font-bold text-white">
                  T&M #{tandm.timeAndMaterialNumber}
                </h2>
                <StatusPill status={tandm.status} />
                {tandm.linkedChangeOrderNumber && (
                  <span className="text-xs font-bold text-purple-300 bg-purple-900 px-2 py-0.5 rounded-full border border-purple-700">
                    → CO #{tandm.linkedChangeOrderNumber}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-300 mt-1 font-medium">
                {tandm.scopeOfWork}
              </p>
              <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 flex-wrap">
                {tandm.date && (
                  <span>{new Date(tandm.date).toLocaleDateString()}</span>
                )}
                <span>
                  Prepared by{" "}
                  <span className="text-gray-300 font-medium">
                    {preparedByName}
                  </span>
                </span>
                <span>{(tandm.scopes || []).length} scope(s)</span>
                <span>{totalItems} item(s)</span>
                {approved.length > 0 && (
                  <span className="text-green-400 font-medium">
                    {approved.length} approved
                  </span>
                )}
                {unconverted.length > 0 && (
                  <span className="text-purple-400 font-medium">
                    {unconverted.length} ready for CO
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canEditHeader && (
                <button
                  onClick={openHeaderEdit}
                  className="px-2.5 py-1.5 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg border border-gray-600 transition-colors"
                >
                  ✎ Edit
                </button>
              )}
              {(tandm.scopes || []).some((s) => s.status === "Approved") && (
                <ExportAllButton
                  tandm={tandm}
                  project={project}
                  companyProfile={companyProfile}
                  tandms={tandms}
                />
              )}
              {tandm.status === "Pending" && allApproved && !approvingAll && (
                <button
                  onClick={() => setApprovingAll(true)}
                  className="px-3 py-1.5 text-xs font-bold bg-green-700 hover:bg-green-600 text-white rounded-lg transition-colors"
                >
                  Approve T&M
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="px-3.5 py-2.5 bg-red-900 border border-red-700 rounded-lg text-xs text-red-300">
          {error}
        </div>
      )}

      {approvingAll && (
        <div className="bg-gray-800 border border-green-700 rounded-xl p-4 space-y-4">
          <h4 className="text-sm font-bold text-white">
            Approve T&M #{tandm.timeAndMaterialNumber}
          </h4>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
              Approved By
            </label>
            <input
              type="text"
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              placeholder="Supervisor name"
              className="w-full px-3 py-2.5 text-sm bg-gray-900 border border-gray-600 rounded-lg text-gray-200 focus:border-green-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
              Signature
            </label>
            <SignaturePad
              captured={sigCaptured}
              onCapture={(b) => {
                setSigBlob(b);
                setSigCaptured(true);
              }}
              onClear={() => {
                setSigBlob(null);
                setSigCaptured(false);
              }}
            />
          </div>
          <div className="flex gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={handleApproveTandM}
              disabled={saving}
              className={cls(
                "px-5 py-2 text-sm font-bold rounded-lg text-white",
                saving
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-600",
              )}
            >
              {saving ? "Saving…" : "Confirm Approval"}
            </button>
            <button
              onClick={() => setApprovingAll(false)}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {sortedScopes.map((scope) => (
          <ScopeCard
            key={scope._id}
            scope={scope}
            tandmId={tandm._id}
            projectId={projectId}
            token={token}
            labourItems={labourItems}
            onTandMUpdated={onUpdated}
            tandm={tandm}
            project={project}
            companyProfile={companyProfile}
            tandms={tandms}
          />
        ))}
        {!(tandm.scopes || []).length && !addingScope && (
          <div className="border-2 border-dashed border-gray-700 rounded-xl py-14 text-center">
            <p className="text-3xl mb-2">📋</p>
            <p className="text-sm text-gray-400 font-medium">No scopes yet</p>
            <p className="text-xs text-gray-600 mt-1">
              Add a scope to start logging work items.
            </p>
          </div>
        )}
      </div>

      {tandm.status !== "Converted" &&
        (addingScope ? (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
            <h4 className="text-sm font-bold text-white mb-4">Add Scope</h4>
            <ScopeForm
              projectId={projectId}
              token={token}
              labourItems={labourItems}
              tandmId={tandm._id}
              onSaved={(t) => {
                setAddingScope(false);
                onUpdated(t);
              }}
              onCancel={() => setAddingScope(false)}
            />
          </div>
        ) : (
          <button
            onClick={() => setAddingScope(true)}
            className="w-full py-3 text-sm font-medium text-gray-500 hover:text-white border-2 border-dashed border-gray-700 hover:border-orange-500 rounded-xl transition-colors"
          >
            + Add Scope
          </button>
        ))}

      {tandm.signature && (
        <div className="flex items-center gap-4 px-4 py-3 bg-gray-800 rounded-lg border border-gray-700 text-xs">
          <span className="text-gray-500">Overall signature:</span>
          <span className="text-gray-300 font-medium">
            {tandm.signedBy || "—"}
          </span>
          <a
            href={tandm.signature}
            target="_blank"
            rel="noreferrer"
            className="text-orange-400 hover:text-orange-300"
          >
            View ↗
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────
export default function TimeAndMaterialTab3({ project }) {
  const projectId = project?._id;
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const [tandms, setTandms] = useState([]);
  const [labourItems, setLabourItems] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [labourError, setLabourError] = useState("");
  const [view, setView] = useState("list");
  const [selectedTandM, setSelectedTandM] = useState(null);

  const [showCreate, setShowCreate] = useState(false);

  // Persisted list state (survives detail navigation)
  const listState = useRef({
    statusFilter: "all",
    search: "",
    sortKey: "createdAt",
    sortDir: "desc",
  });
  const [statusFilter2, setStatusFilter2] = useState(
    listState.current.statusFilter,
  );
  const [search, setSearch] = useState(listState.current.search);
  const [sortKey, setSortKey] = useState(listState.current.sortKey);
  const [sortDir, setSortDir] = useState(listState.current.sortDir);
  const updateListState = (patch) => {
    Object.assign(listState.current, patch);
  };

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    await Promise.allSettled([
      axios
        .get(`${API}/tandm3/project/${projectId}`, { headers })
        .then((r) => setTandms(r.data.tandms || []))
        .catch(() => setTandms([])),
      axios
        .get(`${API}/tandm3/project/${projectId}/labour-items`, { headers })
        .then((r) => {
          setLabourItems(r.data.items || []);
          setLabourError("");
        })
        .catch((err) => {
          setLabourItems([]);
          setLabourError(
            err.response?.data?.message || "Could not load labour rates.",
          );
        }),
      axios
        .get(`${API}/company-profile`, { headers })
        .then((r) => setCompanyProfile(r.data))
        .catch(() => setCompanyProfile(null)),
    ]);
    setLoading(false);
  }, [projectId, headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    let list =
      statusFilter2 === "all"
        ? tandms
        : tandms.filter((t) => t.status === statusFilter2);

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) =>
          String(t.timeAndMaterialNumber).toLowerCase().includes(q) ||
          (t.scopeOfWork || "").toLowerCase().includes(q) ||
          (t.status || "").toLowerCase().includes(q),
      );
    }

    list = [...list].sort((a, b) => {
      let av, bv;
      if (sortKey === "number") {
        av = a.timeAndMaterialNumber ?? 0;
        bv = b.timeAndMaterialNumber ?? 0;
      } else if (sortKey === "date") {
        av = a.date ? new Date(a.date).getTime() : 0;
        bv = b.date ? new Date(b.date).getTime() : 0;
      } else if (sortKey === "createdAt") {
        av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      } else if (sortKey === "status") {
        const order = { Pending: 0, Approved: 1, Rejected: 2, Converted: 3 };
        av = order[a.status] ?? 99;
        bv = order[b.status] ?? 99;
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });

    return list;
  }, [tandms, statusFilter2, search, sortKey, sortDir]);

  const openDetail = (t) => {
    setSelectedTandM(t);
    setView("detail");
  };
  const handleUpdated = (updated) => {
    setTandms((p) => p.map((t) => (t._id === updated._id ? updated : t)));
    setSelectedTandM(updated);
  };

  const summary = useMemo(
    () => ({
      pending: tandms.filter((t) => t.status === "Pending").length,
      approved: tandms.filter((t) => t.status === "Approved").length,
      converted: tandms.filter((t) => t.status === "Converted").length,
      readyForCO: tandms.reduce(
        (n, t) =>
          n +
          (t.scopes || []).filter(
            (s) => s.status === "Approved" && !s.convertedToChangeOrder,
          ).length,
        0,
      ),
    }),
    [tandms],
  );

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 gap-3">
        <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-700 border-t-orange-500" />
        <span className="text-gray-400 text-sm">Loading T&M entries…</span>
      </div>
    );

  return (
    <div className="space-y-5">
      {labourError && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-yellow-900 border border-yellow-700 rounded-lg text-xs text-yellow-300">
          <span>⚠</span>
          <span>
            Labour rates unavailable: {labourError} — ensure{" "}
            <code className="font-mono bg-gray-800 px-1 rounded">
              /api/tandm3
            </code>{" "}
            is registered in your Express app.
          </span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Pending",
            value: summary.pending,
            topBar: "bg-yellow-500",
            numColor: "text-yellow-400",
          },
          {
            label: "Approved",
            value: summary.approved,
            topBar: "bg-green-500",
            numColor: "text-green-400",
          },
          {
            label: "Converted",
            value: summary.converted,
            topBar: "bg-purple-500",
            numColor: "text-purple-400",
          },
          {
            label: "Ready for CO",
            value: summary.readyForCO,
            topBar: "bg-orange-500",
            numColor: "text-orange-400",
          },
        ].map(({ label, value, topBar, numColor }) => (
          <div
            key={label}
            className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden"
          >
            <div className={cls("h-1", topBar)} />
            <div className="px-4 py-3 text-center">
              <p className={cls("text-2xl font-bold", numColor)}>{value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          {view === "detail" && (
            <button
              onClick={() => {
                setView("list");
                setSelectedTandM(null);
                setShowCreate(false);
              }}
              className="text-sm text-gray-400 bg-gray-800 hover:text-white px-3 py-1.5 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
            >
              ← All T&Ms
            </button>
          )}
        </div>
        {view === "list" && !showCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 text-sm font-bold bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
          >
            + New T&M
          </button>
        )}
      </div>

      {showCreate && view === "list" && (
        <TandMCreateForm
          projectId={projectId}
          token={token}
          onCreated={(t) => {
            setTandms((p) => [t, ...p]);
            setShowCreate(false);
            openDetail(t);
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {view === "detail" && selectedTandM && (
        <TandMDetail
          tandm={selectedTandM}
          projectId={projectId}
          token={token}
          tandms={tandms} // ← add this
          labourItems={labourItems}
          onUpdated={handleUpdated}
          project={project}
          companyProfile={companyProfile}
          onBack={() => {
            setView("list");
            setSelectedTandM(null);
          }}
        />
      )}

      {view === "list" && !showCreate && (
        <div className="space-y-3">
          {/* Search + filter bar */}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                updateListState({ search: e.target.value });
              }}
              placeholder="Search T&M #, scope, status…"
              className="flex-1 min-w-[180px] px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
            />
            <select
              value={statusFilter2}
              onChange={(e) => {
                setStatusFilter2(e.target.value);
                updateListState({ statusFilter: e.target.value });
              }}
              className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
            >
              <option value="all">All Statuses</option>
              {["Pending", "Approved", "Converted", "Rejected"].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full text-sm bg-gray-800">
              <thead className="bg-gray-900">
                <tr className="border-b border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider w-10">
                    #
                  </th>
                  {[
                    { key: "number", label: "T&M #" },
                    { key: null, label: "Scope of Work" },
                    { key: "date", label: "Date" },
                    { key: null, label: "Scopes" },

                    { key: "status", label: "Status" },
                    { key: null, label: "Prepared By" },
                  ].map(({ key, label }) =>
                    key ? (
                      <th
                        key={label}
                        onClick={() => {
                          const next =
                            sortKey === key && sortDir === "asc"
                              ? "desc"
                              : "asc";
                          setSortKey(key);
                          setSortDir(next);
                          updateListState({ sortKey: key, sortDir: next });
                        }}
                        className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-orange-400 transition-colors"
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          <span className="text-[10px] text-gray-600">
                            {sortKey === key
                              ? sortDir === "asc"
                                ? "▲"
                                : "▼"
                              : "⇅"}
                          </span>
                        </span>
                      </th>
                    ) : (
                      <th
                        key={label}
                        className={cls(
                          "px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider",
                          label === "Scopes" || label === "Items"
                            ? "text-center"
                            : "text-left",
                        )}
                      >
                        {label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filtered.map((t, i) => {
                  const totalItems = (t.scopes || []).reduce(
                    (n, s) => n + (s.items?.length || 0),
                    0,
                  );
                  const approvedCount = (t.scopes || []).filter(
                    (s) => s.status === "Approved",
                  ).length;
                  const hasReady = (t.scopes || []).some(
                    (s) => s.status === "Approved" && !s.convertedToChangeOrder,
                  );
                  const bar = STATUS[t.status]?.bar || "bg-gray-500";
                  return (
                    <tr
                      key={t._id}
                      onClick={() => openDetail(t)}
                      className={cls(
                        "cursor-pointer hover:bg-gray-700 transition-colors",
                        i % 2 === 0 ? "bg-gray-800" : "bg-gray-750",
                      )}
                    >
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-gray-500 font-medium">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className={cls(
                              "w-1 h-8 rounded-full shrink-0",
                              bar,
                            )}
                          />
                          <span className="font-mono font-bold text-orange-400">
                            #{t.timeAndMaterialNumber}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-200 font-medium truncate max-w-xs">
                          {t.scopeOfWork || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {t.date ? new Date(t.date).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-gray-300 font-medium">
                          {(t.scopes || []).length}
                        </span>
                        {approvedCount > 0 && (
                          <span className="ml-1 text-xs text-green-400">
                            ({approvedCount}✓)
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          <StatusPill status={t.status} size="xs" />
                          {hasReady && (
                            <span className="text-xs font-bold px-1.5 py-0.5 bg-purple-900 text-purple-300 border border-purple-700 rounded-full">
                              CO ready
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {t.preparedBy?.displayName ||
                          t.createdBy?.displayName ||
                          "—"}
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-16 text-center bg-gray-800"
                    >
                      <p className="text-3xl mb-3">📋</p>
                      <p className="text-sm text-gray-400 font-medium">
                        {search.trim()
                          ? "No T&M entries match your search"
                          : statusFilter2 === "all"
                            ? "No T&M entries yet"
                            : `No ${statusFilter2} T&M entries`}
                      </p>
                      {!search.trim() && statusFilter2 === "all" && (
                        <button
                          onClick={() => setShowCreate(true)}
                          className="mt-2 text-sm text-orange-400 hover:text-orange-300 transition-colors"
                        >
                          Create your first T&M →
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
